import axiosHttp from "services/axiosHttp";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { BACKOFFICE_ORDER_REQUEST } from "../constants/apiPaths";
import { reviseOrderRequest as reviseOrderRequestApi } from "../services/orderRequest.api";

/** POST /orders/:orderId/revise — replace a placed sales order's line items (quantity edit, add/remove products). */
export const reviseOrderRequest = createAsyncThunk(
	"orderRequests/reviseOrderRequest",
	async ({ orderId, warehouseId, items } = {}, { rejectWithValue }) => {
		try {
			return await reviseOrderRequestApi({ orderId, warehouseId, items });
		} catch (err) {
			return rejectWithValue(err?.response?.data?.message || err?.message || "Failed to revise order");
		}
	}
);

export const createOrderRequest = createAsyncThunk(
	"orderRequests/createOrderRequest",
	async ({ payload } = {}, { rejectWithValue }) => {
		try {
			const response = await axiosHttp.post(BACKOFFICE_ORDER_REQUEST, payload);
			return response.data?.data ?? response.data;
		} catch (err) {
			return rejectWithValue(err?.response?.data?.message || err?.message || "Failed to create order request");
		}
	}
);

export const patchOrderRequest = createAsyncThunk(
	"orderRequests/patchOrderRequest",
	async ({ id, payload } = {}, { rejectWithValue }) => {
		try {
			const response = await axiosHttp.patch(`${BACKOFFICE_ORDER_REQUEST}/${id}`, payload);
			return response.data?.data ?? response.data;
		} catch (err) {
			return rejectWithValue(err?.response?.data?.message || err?.message || "Failed to patch order request");
		}
	}
);

export const approveOrderRequest = createAsyncThunk(
	"orderRequests/approveOrderRequest",
	async ({ id } = {}, { rejectWithValue }) => {
		try {
			const response = await axiosHttp.post(`${BACKOFFICE_ORDER_REQUEST}/${id}/approve`, {});
			return response.data?.data ?? response.data;
		} catch (err) {
			return rejectWithValue(err?.response?.data?.message || err?.message || "Failed to approve order request");
		}
	}
);

export const approveOrderRequestLine = createAsyncThunk(
	"orderRequests/approveOrderRequestLine",
	async ({ lineId } = {}, { rejectWithValue }) => {
		try {
			const response = await axiosHttp.post(`${BACKOFFICE_ORDER_REQUEST}/line/${lineId}/approve`, {});
			return response.data?.data ?? response.data;
		} catch (err) {
			return rejectWithValue(err?.response?.data?.message || err?.message || "Failed to approve order request line");
		}
	}
);

export const rejectOrderRequest = createAsyncThunk(
	"orderRequests/rejectOrderRequest",
	async ({ id, reason } = {}, { rejectWithValue }) => {
		try {
			const response = await axiosHttp.post(`${BACKOFFICE_ORDER_REQUEST}/${id}/reject`, { reason: reason || "" });
			return response.data?.data ?? response.data;
		} catch (err) {
			return rejectWithValue(err?.response?.data?.message || err?.message || "Failed to reject order request");
		}
	}
);

export const rejectOrderRequestLine = createAsyncThunk(
	"orderRequests/rejectOrderRequestLine",
	async ({ lineId, reason } = {}, { rejectWithValue }) => {
		try {
			const response = await axiosHttp.post(`${BACKOFFICE_ORDER_REQUEST}/line/${lineId}/reject`, { reason: reason || "" });
			return response.data?.data ?? response.data;
		} catch (err) {
			return rejectWithValue(err?.response?.data?.message || err?.message || "Failed to reject order request line");
		}
	}
);

export const promoteOrderRequest = createAsyncThunk(
	"orderRequests/promoteOrderRequest",
	async ({ id, channel_id } = {}, { rejectWithValue }) => {
		try {
			const response = await axiosHttp.post(`${BACKOFFICE_ORDER_REQUEST}/${id}/promote`, channel_id ? { channel_id } : {});
			return response.data?.data ?? response.data;
		} catch (err) {
			return rejectWithValue(err?.response?.data?.message || err?.message || "Failed to convert order request");
		}
	}
);

const OrderRequestActionsSlice = createSlice({
	name: "OrderRequestActionsSlice",
	initialState: {
		isLoading: false,
		error: null,
		lastResult: null,
	},
	extraReducers: (builder) => {
		const handlePending = (state) => {
			state.isLoading = true;
			state.error = null;
		};

		const handleRejected = (state, action) => {
			state.isLoading = false;
			state.error = action.payload || action.error?.message;
		};

		const handleFulfilled = (state, action) => {
			state.isLoading = false;
			state.error = null;
			state.lastResult = action.payload;
		};

		builder
			.addCase(reviseOrderRequest.pending, handlePending)
			.addCase(reviseOrderRequest.fulfilled, handleFulfilled)
			.addCase(reviseOrderRequest.rejected, handleRejected)
			.addCase(createOrderRequest.pending, handlePending)
			.addCase(createOrderRequest.fulfilled, handleFulfilled)
			.addCase(createOrderRequest.rejected, handleRejected)
			.addCase(patchOrderRequest.pending, handlePending)
			.addCase(patchOrderRequest.fulfilled, handleFulfilled)
			.addCase(patchOrderRequest.rejected, handleRejected)
			.addCase(approveOrderRequest.pending, handlePending)
			.addCase(approveOrderRequest.fulfilled, handleFulfilled)
			.addCase(approveOrderRequest.rejected, handleRejected)
			.addCase(approveOrderRequestLine.pending, handlePending)
			.addCase(approveOrderRequestLine.fulfilled, handleFulfilled)
			.addCase(approveOrderRequestLine.rejected, handleRejected)
			.addCase(rejectOrderRequest.pending, handlePending)
			.addCase(rejectOrderRequest.fulfilled, handleFulfilled)
			.addCase(rejectOrderRequest.rejected, handleRejected)
			.addCase(rejectOrderRequestLine.pending, handlePending)
			.addCase(rejectOrderRequestLine.fulfilled, handleFulfilled)
			.addCase(rejectOrderRequestLine.rejected, handleRejected)
			.addCase(promoteOrderRequest.pending, handlePending)
			.addCase(promoteOrderRequest.fulfilled, handleFulfilled)
			.addCase(promoteOrderRequest.rejected, handleRejected);
	},
});

export default OrderRequestActionsSlice.reducer;

